import { useEffect, useRef, useState } from 'react';
import { loadFaceDetector, type DetectorLoader, type FaceDetectorLike } from './detector';

export const SAMPLE_INTERVAL_MS = 250; // ~4 fps
/** presentForMs is capped: nothing downstream cares beyond a few seconds, and it keeps renders quiet. */
const PRESENT_CAP_MS = 5000;

export interface FaceCheckState {
  /** Detector loaded and sampling. */
  ready: boolean;
  /** Loading or running the detector failed. Callers fail open. */
  error: unknown;
  faceVisible: boolean;
  /** How long the face has been continuously absent (0 while visible). */
  absentForMs: number;
  /** How long the face has been continuously visible (0 while absent), capped at 5 s. */
  presentForMs: number;
}

export type FaceSource = HTMLVideoElement | MediaStreamTrack | null | undefined;

export interface UseFaceCheckOptions {
  enabled?: boolean;
  intervalMs?: number;
  /** Injected in tests. */
  loader?: DetectorLoader;
  now?: () => number;
}

const INITIAL: FaceCheckState = { ready: false, error: null, faceVisible: false, absentForMs: 0, presentForMs: 0 };

function isTrack(s: FaceSource): s is MediaStreamTrack {
  return !!s && typeof (s as MediaStreamTrack).kind === 'string' && typeof (s as MediaStreamTrack).stop === 'function';
}

function frameReady(v: HTMLVideoElement | null): v is HTMLVideoElement {
  return !!v && v.readyState >= 2 && v.videoWidth > 0 && !v.paused;
}

/**
 * Samples a video element (or a camera MediaStreamTrack) at ~4 fps and reports
 * whether a face is in frame. Runs entirely on-device; frames are never stored
 * or sent anywhere. A missing/blank source counts as "no face".
 */
export function useFaceCheck(source: FaceSource, opts: UseFaceCheckOptions = {}): FaceCheckState {
  const { enabled = true, intervalMs = SAMPLE_INTERVAL_MS, loader = loadFaceDetector, now = Date.now } = opts;
  const [state, setState] = useState<FaceCheckState>(INITIAL);
  const [detector, setDetector] = useState<FaceDetectorLike | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const nowRef = useRef(now);
  nowRef.current = now;

  // 1. Lazy-load the detector.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loader()
      .then((d) => {
        if (!cancelled) setDetector(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setState((s) => ({ ...s, error: e, ready: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, loader]);

  // 2. Resolve the source into a <video> element (tracks get a hidden one).
  useEffect(() => {
    if (!isTrack(source)) {
      videoRef.current = (source as HTMLVideoElement | null | undefined) ?? null;
      return;
    }
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    try {
      v.srcObject = new MediaStream([source]);
    } catch {
      /* jsdom / very old browsers */
    }
    void v.play?.()?.catch?.(() => undefined);
    videoRef.current = v;
    return () => {
      v.srcObject = null;
      videoRef.current = null;
    };
  }, [source]);

  // 3. Sample.
  useEffect(() => {
    if (!enabled || !detector) return;
    let visibleSince: number | null = null;
    let absentSince: number | null = null;
    const tick = () => {
      const t = nowRef.current();
      const v = videoRef.current;
      let face = false;
      let err: unknown = null;
      if (frameReady(v)) {
        try {
          face = detector.hasFace(v, performance.now());
        } catch (e) {
          err = e;
        }
      }
      if (face) {
        absentSince = null;
        visibleSince ??= t;
      } else {
        visibleSince = null;
        absentSince ??= t;
      }
      const next: FaceCheckState = {
        ready: true,
        error: err,
        faceVisible: face,
        absentForMs: absentSince === null ? 0 : t - absentSince,
        presentForMs: visibleSince === null ? 0 : Math.min(PRESENT_CAP_MS, t - visibleSince),
      };
      setState((s) =>
        s.ready === next.ready && s.error === next.error && s.faceVisible === next.faceVisible && s.absentForMs === next.absentForMs && s.presentForMs === next.presentForMs ? s : next,
      );
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [enabled, detector, intervalMs]);

  if (!enabled) return INITIAL;
  return state;
}
